/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Editor from "@monaco-editor/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Badge } from "@/shared/components/ui/badge";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { useTheme } from "@/shared/hooks/use-theme";
import {
  ChartType,
  CreateTemplateInput,
  DashboardTemplate,
  DashboardTemplateScope,
  DashboardTemplatesAdminService,
  MetricCatalogItem,
} from "../services/dashboardTemplatesAdminService";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: DashboardTemplate;
}

const CHART_TYPES: ChartType[] = [
  "line",
  "bar",
  "scatter",
  "area",
  "pie",
  "donut",
  "gauge",
  "gauge-multiple",
];

const SCOPES: DashboardTemplateScope[] = ["superadmin", "organization", "product"];

function stringify(value: unknown, fallback = ""): string {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function safeParseJson<T = any>(raw: string, fallback: T): T | { __error: string } {
  if (!raw.trim()) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch (err: any) {
    return { __error: err.message };
  }
}

function bilingualName(t: DashboardTemplate["name"] | string | undefined): { en: string; pt: string } {
  if (!t) return { en: "", pt: "" };
  if (typeof t === "string") return { en: t, pt: t };
  return { en: t.en || "", pt: t.pt || "" };
}

export function DashboardTemplateEditorDialog({ open, onOpenChange, template }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { theme } = useTheme();
  const monacoTheme = theme === "dark" ? "vs-dark" : "light";
  const isEditing = !!template;

  const [nameEn, setNameEn] = useState("");
  const [namePt, setNamePt] = useState("");
  const [descEn, setDescEn] = useState("");
  const [descPt, setDescPt] = useState("");
  const [category, setCategory] = useState("");
  const [icon, setIcon] = useState("");
  const [type, setType] = useState<ChartType>("bar");
  const [scope, setScope] = useState<DashboardTemplateScope>("organization");
  const [isActive, setIsActive] = useState(true);

  // JSON-edited fields
  const [dataSourcesJson, setDataSourcesJson] = useState("[]");
  const [transformJson, setTransformJson] = useState('{"type":"default"}');
  const [filtersJson, setFiltersJson] = useState("[]");
  const [refreshJson, setRefreshJson] = useState('{"enabled":false,"interval":0}');

  // Preview state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<any>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Load metric catalog (for hint in datasources tab)
  const { data: catalog } = useQuery<MetricCatalogItem[]>({
    queryKey: ["admin-dashboard-templates-metrics-catalog"],
    queryFn: () => DashboardTemplatesAdminService.getMetricsCatalog(),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    if (template) {
      const n = bilingualName(template.name);
      const d = bilingualName(template.description);
      setNameEn(n.en);
      setNamePt(n.pt);
      setDescEn(d.en);
      setDescPt(d.pt);
      setCategory(template.category ?? "");
      setIcon(template.icon ?? "");
      setType(template.type);
      setScope(template.scope);
      setIsActive(template.is_active);
      setDataSourcesJson(stringify(template.data_sources ?? [], "[]"));
      setTransformJson(stringify(template.transform ?? { type: "default" }, '{"type":"default"}'));
      setFiltersJson(stringify(template.default_filters ?? [], "[]"));
      setRefreshJson(stringify(template.refresh_config ?? { enabled: false, interval: 0 }, '{"enabled":false,"interval":0}'));
    } else {
      setNameEn(""); setNamePt(""); setDescEn(""); setDescPt("");
      setCategory(""); setIcon(""); setType("bar"); setScope("organization"); setIsActive(true);
      setDataSourcesJson("[]");
      setTransformJson('{"type":"default"}');
      setFiltersJson("[]");
      setRefreshJson('{"enabled":false,"interval":0}');
    }
    setPreviewResult(null);
    setPreviewError(null);
  }, [open, template]);

  const buildPayload = (): { ok: true; payload: CreateTemplateInput } | { ok: false; error: string } => {
    if (!nameEn.trim() || !namePt.trim()) {
      return { ok: false, error: t("settings.dashboardTemplateEditor.nameRequired") };
    }
    const dataSources = safeParseJson<any[]>(dataSourcesJson, []);
    if ((dataSources as any).__error) return { ok: false, error: `data_sources: ${(dataSources as any).__error}` };
    const transform = safeParseJson<any>(transformJson, { type: "default" });
    if ((transform as any).__error) return { ok: false, error: `transform: ${(transform as any).__error}` };
    const filters = safeParseJson<any[]>(filtersJson, []);
    if ((filters as any).__error) return { ok: false, error: `default_filters: ${(filters as any).__error}` };
    const refresh = safeParseJson<any>(refreshJson, { enabled: false, interval: 0 });
    if ((refresh as any).__error) return { ok: false, error: `refresh_config: ${(refresh as any).__error}` };

    const payload: CreateTemplateInput = {
      name: { en: nameEn.trim(), pt: namePt.trim() },
      description:
        descEn.trim() || descPt.trim()
          ? { en: descEn.trim(), pt: descPt.trim() }
          : undefined,
      category: category.trim() || undefined,
      icon: icon.trim() || undefined,
      type,
      scope,
      is_active: isActive,
      data_sources: dataSources as any[],
      transform: transform,
      default_filters: filters as any[],
      refresh_config: refresh,
    };
    return { ok: true, payload };
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const built = buildPayload();
      if (!built.ok) throw new Error(built.error);
      if (template) {
        return DashboardTemplatesAdminService.update(template.id, built.payload);
      }
      return DashboardTemplatesAdminService.create(built.payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard-templates"] });
      toast.success(isEditing ? t("settings.dashboardTemplateEditor.templateUpdated") : t("settings.dashboardTemplateEditor.templateCreated"));
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handlePreview = async () => {
    setPreviewError(null);
    setPreviewLoading(true);
    setPreviewResult(null);
    try {
      const built = buildPayload();
      if (!built.ok) throw new Error(built.error);
      const result = await DashboardTemplatesAdminService.preview({
        data_sources: built.payload.data_sources,
        transform: built.payload.transform,
        default_filters: built.payload.default_filters,
        type: built.payload.type,
        refresh_config: built.payload.refresh_config,
        name: built.payload.name,
      });
      setPreviewResult(result);
    } catch (err: any) {
      setPreviewError(err?.message ?? t("settings.dashboardTemplateEditor.unknownError"));
    } finally {
      setPreviewLoading(false);
    }
  };

  const catalogByScope = useMemo(
    () =>
      (catalog ?? []).filter(
        (m) => m.scope === "any" || m.scope === scope
      ),
    [catalog, scope]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("settings.dashboardTemplateEditor.editTitle") : t("settings.dashboardTemplateEditor.newTitle")}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="basic" className="mt-2">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="basic">{t("settings.dashboardTemplateEditor.tabBasic")}</TabsTrigger>
            <TabsTrigger value="datasources">{t("settings.dashboardTemplateEditor.tabDatasources")}</TabsTrigger>
            <TabsTrigger value="advanced">{t("settings.dashboardTemplateEditor.tabAdvanced")}</TabsTrigger>
            <TabsTrigger value="preview">{t("settings.dashboardTemplateEditor.tabPreview")}</TabsTrigger>
          </TabsList>

          {/* ─── Basic ────────────────────────────────────────── */}
          <TabsContent value="basic" className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{t("settings.dashboardTemplateEditor.nameEn")}</Label>
                <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder={t("settings.dashboardTemplateEditor.namePlaceholderEn")} />
              </div>
              <div className="space-y-1">
                <Label>{t("settings.dashboardTemplateEditor.namePt")}</Label>
                <Input value={namePt} onChange={(e) => setNamePt(e.target.value)} placeholder={t("settings.dashboardTemplateEditor.namePlaceholderPt")} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{t("settings.dashboardTemplateEditor.descEn")}</Label>
                <Input value={descEn} onChange={(e) => setDescEn(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>{t("settings.dashboardTemplateEditor.descPt")}</Label>
                <Input value={descPt} onChange={(e) => setDescPt(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label>{t("settings.dashboardTemplateEditor.category")}</Label>
                <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="platform" />
              </div>
              <div className="space-y-1">
                <Label>{t("settings.dashboardTemplateEditor.icon")}</Label>
                <Input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="BarChart3" />
              </div>
              <div className="space-y-1">
                <Label>{t("settings.dashboardTemplateEditor.type")}</Label>
                <Select value={type} onValueChange={(v) => setType(v as ChartType)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CHART_TYPES.map((ct) => (
                      <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>{t("settings.dashboardTemplateEditor.scope")}</Label>
                <Select value={scope} onValueChange={(v) => setScope(v as DashboardTemplateScope)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCOPES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={isActive} onCheckedChange={setIsActive} />
              <Label>{t("settings.dashboardTemplateEditor.active")}</Label>
            </div>
          </TabsContent>

          {/* ─── Datasources ─────────────────────────────────── */}
          <TabsContent value="datasources" className="space-y-3 py-4">
            <Alert>
              <AlertDescription className="text-xs">
                {t("settings.dashboardTemplateEditor.datasourcesHint1")} <code>kind: 'http' | 'internal'</code>.
                {" "}{t("settings.dashboardTemplateEditor.datasourcesHint2")} <code>internal</code> {t("settings.dashboardTemplateEditor.datasourcesHint3")}
              </AlertDescription>
            </Alert>

            <div className="border rounded-md p-3 space-y-1 bg-muted/40">
              <Label className="text-xs uppercase tracking-wide">{t("settings.dashboardTemplateEditor.metricsCatalog")}</Label>
              {catalogByScope.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t("settings.dashboardTemplateEditor.noMetrics")}</p>
              ) : (
                <div className="space-y-2 max-h-44 overflow-y-auto">
                  {catalogByScope.map((m) => (
                    <div key={m.id} className="text-xs">
                      <div className="flex items-center gap-2">
                        <code className="font-mono bg-background px-1.5 py-0.5 rounded">{m.id}</code>
                        <Badge variant="outline" className="text-[10px]">{m.scope}</Badge>
                      </div>
                      <div className="text-muted-foreground mt-0.5">{m.description}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        params: {m.params.map((p) => p.name).join(", ") || "—"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label>data_sources (JSON)</Label>
              <div className="border rounded-md overflow-hidden">
                <Editor
                  height="260px"
                  defaultLanguage="json"
                  value={dataSourcesJson}
                  onChange={(v) => setDataSourcesJson(v ?? "")}
                  theme={monacoTheme}
                  options={{ minimap: { enabled: false }, fontSize: 12 }}
                />
              </div>
            </div>
          </TabsContent>

          {/* ─── Advanced ────────────────────────────────────── */}
          <TabsContent value="advanced" className="space-y-3 py-4">
            <div className="space-y-1">
              <Label>transform (JSON)</Label>
              <div className="border rounded-md overflow-hidden">
                <Editor
                  height="160px"
                  defaultLanguage="json"
                  value={transformJson}
                  onChange={(v) => setTransformJson(v ?? "")}
                  theme={monacoTheme}
                  options={{ minimap: { enabled: false }, fontSize: 12 }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>default_filters (JSON)</Label>
              <div className="border rounded-md overflow-hidden">
                <Editor
                  height="160px"
                  defaultLanguage="json"
                  value={filtersJson}
                  onChange={(v) => setFiltersJson(v ?? "")}
                  theme={monacoTheme}
                  options={{ minimap: { enabled: false }, fontSize: 12 }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>refresh_config (JSON)</Label>
              <div className="border rounded-md overflow-hidden">
                <Editor
                  height="100px"
                  defaultLanguage="json"
                  value={refreshJson}
                  onChange={(v) => setRefreshJson(v ?? "")}
                  theme={monacoTheme}
                  options={{ minimap: { enabled: false }, fontSize: 12 }}
                />
              </div>
            </div>
          </TabsContent>

          {/* ─── Preview ─────────────────────────────────────── */}
          <TabsContent value="preview" className="space-y-3 py-4">
            <p className="text-xs text-muted-foreground">
              {t("settings.dashboardTemplateEditor.previewHint")}
            </p>
            <Button onClick={handlePreview} disabled={previewLoading} size="sm">
              {previewLoading ? t("settings.dashboardTemplateEditor.runningPreview") : t("settings.dashboardTemplateEditor.runPreview")}
            </Button>
            {previewError && (
              <Alert variant="destructive">
                <AlertDescription>{previewError}</AlertDescription>
              </Alert>
            )}
            {previewResult && (
              <div className="border rounded-md overflow-hidden">
                <Editor
                  height="360px"
                  defaultLanguage="json"
                  value={JSON.stringify(previewResult, null, 2)}
                  theme={monacoTheme}
                  options={{ readOnly: true, minimap: { enabled: false }, fontSize: 12 }}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saveMutation.isPending}>
            {t("settings.dashboardTemplateEditor.cancel")}
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? t("settings.dashboardTemplateEditor.saving") : isEditing ? t("settings.dashboardTemplateEditor.save") : t("settings.dashboardTemplateEditor.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
